import { useEffect, useState, type CSSProperties } from 'react';
import { RELEASE } from '@crave/shared';
import { Link, Navigate, Route, Routes } from 'react-router';
import type { Session } from '@supabase/supabase-js';
import { siteUrl } from './api.ts';
import { onSession } from './lib/auth.ts';
import { AccountPage } from './pages/AccountPage.tsx';
import { CartPage } from './pages/CartPage.tsx';
import { CheckoutPage } from './pages/CheckoutPage.tsx';
import { ItemPage } from './pages/ItemPage.tsx';
import { MenuPage } from './pages/MenuPage.tsx';
import { TastePage } from './pages/TastePage.tsx';
import { TrackPage } from './pages/TrackPage.tsx';
import { FeedbackPage } from './pages/FeedbackPage.tsx';
import { HowToEatPage } from './pages/HowToEatPage.tsx';
import { TextTermsPage } from './pages/TextTermsPage.tsx';
import { UnpaidBanner } from './components/UnpaidBanner.tsx';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  useEffect(() => onSession(setSession), []);
  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px 96px' }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 0',
        }}
      >
        <Link to="/" aria-label="Crave Pizza home" style={{ display: 'inline-flex' }}>
          <img
            src="/brand/logo-color-480.png"
            alt="Crave Pizza"
            style={{ height: 44, width: 'auto' }}
          />
        </Link>
        <nav
          style={{
            display: 'flex',
            gap: 10,
            fontSize: 14,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <a href={siteUrl} style={{ ...navItem, color: 'var(--ink-soft)' }}>
            About
          </a>
          {session ? (
            <Link to="/account" style={pill(false)}>
              My account
            </Link>
          ) : (
            <>
              <Link to="/account" style={{ ...navItem, color: 'var(--ink)', fontWeight: 600 }}>
                Sign in
              </Link>
              <Link to="/account?mode=signup" style={pill(true)}>
                Create account
              </Link>
            </>
          )}
        </nav>
      </header>
      <UnpaidBanner />
      <Routes>
        <Route path="/" element={<MenuPage />} />
        <Route path="/item/:slug" element={<ItemPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/order/h/:token" element={<TrackPage hashed />} />
        <Route path="/order/:token" element={<TrackPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/login" element={<AccountPage />} />
        <Route path="/signup" element={<Navigate to="/account?mode=signup" replace />} />
        <Route path="/taste/:code" element={<TastePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/feedback/h/:token" element={<FeedbackPage />} />
        <Route path="/how-to-eat" element={<HowToEatPage />} />
        <Route path="/texts" element={<TextTermsPage />} />
        <Route path="*" element={<MenuPage />} />
      </Routes>
      <footer
        style={{
          padding: '28px 20px calc(24px + env(safe-area-inset-bottom))',
          textAlign: 'center',
          color: 'var(--ink-soft)',
          fontSize: 13,
        }}
      >
        <a href="/how-to-eat" style={{ color: 'inherit', fontWeight: 600 }}>
          How to eat a Neapolitan pizza
        </a>
        {' · '}
        <a href="/feedback" style={{ color: 'inherit', fontWeight: 600 }}>
          How was your pizza?
        </a>
        <br />
        Crave Pizza · {RELEASE}
      </footer>
    </div>
  );
}

/**
 * Every link in the header is a full thumb-sized target. They sit in a row next to the logo on a
 * phone, so the extra size is height plus a little side room. That side room is padding the
 * negative margin takes straight back, so the tap area grows while the row stays exactly as narrow
 * as the words: the header still fits on one line on a small phone, and on the first paint before
 * the web font arrives, where the fallback letters are wider.
 */
const navItem: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 'var(--touch)',
  padding: '0 4px',
  margin: '0 -4px',
  whiteSpace: 'nowrap',
};

function pill(filled: boolean) {
  return {
    ...navItem,
    // The pill is its own visible shape, so its side room is real padding, not borrowed space.
    padding: '0 12px',
    margin: 0,
    color: filled ? '#fff' : 'var(--ink)',
    background: filled ? 'var(--ink)' : 'transparent',
    border: '2px solid var(--ink)',
    borderRadius: 999,
    fontWeight: 600,
    textDecoration: 'none',
  };
}
